// RentalDetailPage - Detailed view of a single rental
import { RentalStatus, extendRental, isInventoryCasConflict } from '@/api/rentals';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { useApiError } from '@/hooks/use-api-error';
import { useRental } from '@/hooks/use-rentals';
import { useToast } from '@/hooks/use-toast';
import { formatDateSmart, formatDateTime, formatRelativeTime } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DepositActions } from './components/DepositActions';
import { HandoverPhotoUploader } from './components/HandoverPhotoUploader';
import { InlineSignButton } from './components/InlineSignButton';
import { RentalItemDiscountSection } from './components/RentalItemDiscountSection';
import { RentalPaymentForm } from './components/RentalPaymentForm';
import { calculateExtensionPreview } from './rental-utils';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
  ACTIVE: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200',
  OVERDUE: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200',
  RETURNED: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  COMPLETED: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200',
  CANCELLED: 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200',
};

/** PV-KAUCIO-002 (32-kör): refundStatus badge-szín (PENDING/RECEIVED→szürke, HELD→kék, REFUNDED/RELEASED→zöld, RETAINED→narancs, PARTIAL→sárga) */
const DEPOSIT_STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  RECEIVED: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  HELD: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200',
  RELEASED: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200',
  RETAINED: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200',
  PARTIAL: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function RentalDetailPage() {
  const { t } = useTranslation();

  const STATUS_LABELS: Record<string, string> = {
    DRAFT: t('status.draft'),
    ACTIVE: t('status.active'),
    OVERDUE: t('status.expired'),
    RETURNED: t('status.returned'),
    COMPLETED: t('status.closed'),
    CANCELLED: t('status.cancelled'),
  };
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { toast } = useToast();
  const { getErrorMessage } = useApiError();

  // Fetch rental from API
  const { rental, loading, error, refetch } = useRental(id);

  // Payment form state (PV-BERLES-001 réteg-1)
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Extension dialog state (Story 46-12) — must be before early returns (Rules of Hooks)
  const [extensionOpen, setExtensionOpen] = useState(false);
  const [extensionDate, setExtensionDate] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [extensionSubmitting, setExtensionSubmitting] = useState(false);
  const [extensionError, setExtensionError] = useState<string | null>(null);

  // Escape key + body scroll lock for extension dialog (CR-46-12 #4, #10)
  useEffect(() => {
    if (!extensionOpen) return;
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExtensionOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [extensionOpen]);

  const extensionPreview = useMemo(() => {
    if (
      !extensionDate ||
      !rental?.expectedReturnDate ||
      !rental?.startDate ||
      rental?.dailyRate == null
    )
      return null;
    return calculateExtensionPreview(
      rental.expectedReturnDate,
      rental.startDate,
      new Date(extensionDate),
      rental.dailyRate
    );
  }, [extensionDate, rental?.expectedReturnDate, rental?.startDate, rental?.dailyRate]);

  const handleExtensionOpen = useCallback(() => {
    setExtensionDate('');
    setExtensionReason('');
    setExtensionError(null);
    setExtensionOpen(true);
  }, []);

  const handleExtensionSubmit = useCallback(async () => {
    if (!extensionPreview || !id) return;
    setExtensionSubmitting(true);
    setExtensionError(null);
    try {
      await extendRental(id, {
        newReturnDate: extensionPreview.newEndDate,
        reason: extensionReason || undefined,
      });
      setExtensionOpen(false);
      refetch();
    } catch (err) {
      setExtensionError(err instanceof Error ? err.message : t('rental.extensionError'));
    } finally {
      setExtensionSubmitting(false);
    }
  }, [extensionPreview, id, extensionReason, refetch]);

  // Loading state
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent" />
          <p className="mt-2 text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="p-8 text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-foreground mb-2">{t('common.error')}</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/rental')}>{t('rental.backToRentals')}</Button>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!rental) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="p-8 text-center">
          <div className="text-4xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-foreground mb-2">{t('rental.notFound')}</h2>
          <p className="text-muted-foreground mb-4">{t('rental.notFoundDescription')}</p>
          <Button onClick={() => navigate('/rental')}>{t('rental.backToRentals')}</Button>
        </Card>
      </div>
    );
  }

  const getDaysRemaining = () => {
    const endDate = new Date(rental.expectedReturnDate);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining();

  // Timeline events
  const timelineEvents = [
    {
      date: rental.createdAt,
      icon: '📝',
      title: t('rental.timeline.contractCreated'),
      description: rental.rentalCode,
    },
    {
      date: rental.startDate,
      icon: '🔧',
      title: t('rental.timeline.rentalStarted'),
      description: rental.equipmentName,
    },
    ...(rental.depositPaid > 0
      ? [
          {
            date: rental.createdAt,
            icon: '💰',
            title: t('rental.timeline.depositPaid'),
            description: formatCurrency(rental.depositPaid),
          },
        ]
      : []),
  ];

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/rental')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">{rental.rentalCode}</h1>
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium ${STATUS_COLORS[rental.status] ?? STATUS_COLORS.ACTIVE}`}
                >
                  {STATUS_LABELS[rental.status] ?? rental.status}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{rental.customerName}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {rental.status === RentalStatus.DRAFT && (
              <>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={async () => {
                    try {
                      const { confirmPickup } = await import('@/api/rentals');
                      await confirmPickup(rental.id, {
                        accessoryChecklistVerified: true,
                        depositCollected: true,
                        depositMethod: 'cash', // Q-T138: BE-enum kisbetűs (uppercase → @IsEnum 400)
                      });
                      refetch();
                    } catch (err) {
                      // ADR-137 (#314): foglaltság-CAS ütközés (más pult közben kiadta)
                      // → strukturált 409 `INVENTORY_CAS_CONFLICT` → toast + availability-refresh.
                      if (isInventoryCasConflict(err)) {
                        toast({
                          title: t('rental.pickup.conflictTitle'),
                          description: t('rental.pickup.conflictDesc'),
                          variant: 'destructive',
                        });
                        refetch();
                      } else {
                        // 49Y: a nem-CAS hibák (pl. RENTAL_DEPOSIT_INSUFFICIENT teljes-kaució-gate)
                        // a getErrorMessage-en át → code-alapú lokalizált toast mind a 4 nyelven,
                        // fallback a régi unknownError-üzenetre. Régen a nyers BE-message ment ki
                        // (csak hu-n stimmelt, a többi nyelven magyar szöveget mutatott).
                        toast({
                          title: getErrorMessage(err, t('error.common.unknownError')),
                          variant: 'destructive',
                        });
                      }
                    }
                  }}
                >
                  {t('rental.confirmPickup')}
                </Button>
                {/* B1: az „Aláírás most" CTA csak aláíratlan szerződésen — re-sign
                    guard (#322 contractSignedAt). A pickup-gomb DRAFT-on marad. */}
                {!rental.contractSignedAt && (
                  <InlineSignButton rentalId={rental.id} onSigned={refetch} />
                )}
              </>
            )}
            {(rental.status === RentalStatus.ACTIVE || rental.status === RentalStatus.OVERDUE) && (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/rental/return?id=${rental.id}`)}
                >
                  {t('rental.returnRental')}
                </Button>
                <Button variant="outline" onClick={handleExtensionOpen}>
                  {t('rental.extension')}
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => window.print()}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Status Alert */}
        {rental.status === RentalStatus.OVERDUE && (
          <div className="mb-6 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  {t('rental.overdueAlert', { days: Math.abs(daysRemaining) })}
                </p>
                <p className="text-sm text-red-600 dark:text-red-300">
                  {t('rental.lateFee')}: {formatCurrency(rental.lateFeeAmount)}
                </p>
              </div>
            </div>
          </div>
        )}

        {rental.status === RentalStatus.ACTIVE && daysRemaining <= 2 && daysRemaining >= 0 && (
          <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 p-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {daysRemaining === 0
                    ? t('rental.expiresToday')
                    : t('rental.daysUntilExpiry', { days: daysRemaining })}
                </p>
                <p className="text-sm text-amber-600 dark:text-amber-300">
                  {t('rental.expiryDate')}: {formatDateSmart(rental.expectedReturnDate)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Equipment Card — supports multi-item rentals (rental.items[]) */}
            <Card>
              <CardHeader>
                <CardTitle>{t('rental.rentedEquipment')}</CardTitle>
              </CardHeader>
              <CardContent>
                {rental.items && rental.items.length > 0 ? (
                  <div className="space-y-4">
                    {rental.items.map(item => (
                      <div
                        key={item.id}
                        className="flex items-start gap-4 rounded-lg border p-3 dark:border-slate-600"
                      >
                        <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-700 text-2xl">
                          🔧
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-foreground">{item.equipmentName}</h3>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span>SN: {item.serialNumber}</span>
                            <span>x{item.quantity}</span>
                            {item.depositAmount > 0 && (
                              <span>
                                {t('rental.deposit')}: {formatCurrency(item.depositAmount)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm text-muted-foreground">{t('rental.dailyRate')}</p>
                          <p className="text-lg font-bold text-primary">
                            {formatCurrency(item.dailyRate)}
                          </p>
                          {item.itemTotal > 0 && (
                            <p className="text-xs text-muted-foreground">
                              {t('rental.totalRentalFee')}: {formatCurrency(item.itemTotal)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Legacy single-item fallback */
                  <div className="flex items-start gap-4">
                    <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-gray-100 dark:bg-slate-700 text-3xl">
                      🔧
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-foreground">{rental.equipmentName}</h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full bg-gray-100 dark:bg-slate-700 px-2 py-1 text-xs text-muted-foreground">
                          ID: {rental.equipmentId.substring(0, 8)}...
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">{t('rental.dailyRate')}</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(rental.dailyRate)}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ADR-037 Am-1 — per-gép manuális engedmény (post-creation, csak DRAFT/CONFIRMED) */}
            {(rental.status === RentalStatus.DRAFT || String(rental.status) === 'CONFIRMED') &&
              rental.items &&
              rental.items.length > 0 && (
                <RentalItemDiscountSection
                  rentalId={rental.id}
                  items={rental.items}
                  onApplied={refetch}
                />
              )}

            {/* Partner Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('rental.partnerDetails')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('partner.name')}</p>
                    <p className="font-medium text-foreground">{rental.customerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('rental.partnerIdLabel')}</p>
                    <p className="font-medium text-foreground">
                      {rental.customerId.substring(0, 8)}...
                    </p>
                  </div>
                  {/* TODO: Show partner phone + status badge (TRUSTED/WARNING) once the
                       Rental API response includes partner.phone and partner.status fields.
                       Currently the API only returns customerId + customerName. */}
                </div>
              </CardContent>
            </Card>

            {/* Pricing Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('rental.financialSummary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Rental period */}
                  <div className="flex items-center justify-between py-2 border-b dark:border-slate-600">
                    <span className="text-muted-foreground">{t('rental.period')}</span>
                    <span className="font-medium text-foreground">
                      {formatDateSmart(rental.startDate)} -{' '}
                      {formatDateSmart(rental.expectedReturnDate)}
                    </span>
                  </div>

                  {/* Pricing breakdown */}
                  <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('rental.dailyRate')}</span>
                      <span className="text-foreground">{formatCurrency(rental.dailyRate)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg pt-2 border-t dark:border-slate-600">
                      <span className="text-foreground">{t('rental.totalRentalFee')}</span>
                      <span className="text-primary">{formatCurrency(rental.totalAmount)}</span>
                    </div>
                  </div>

                  {/* Deposit */}
                  <div className="flex items-center justify-between py-2 border-b dark:border-slate-600">
                    <span className="text-muted-foreground">{t('rental.deposit')}</span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(rental.depositAmount)}
                    </span>
                  </div>
                  {/* TODO: Show deposit method (CARD/CASH/TRANSFER) and admin fee once
                       the Rental API response includes depositMethod and adminFee fields. */}

                  {/* PV-KAUCIO-002 (32-kör): per-gép kaució-bontás (detail-only, #681 read-DTO) */}
                  {rental.deposits != null && rental.deposits.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="deposit-breakdown">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('rental.depositBreakdown')}
                      </p>
                      {rental.deposits.map((d, idx) => {
                        const rowKey = d.rentalItemId ?? `legacy-${idx}`;
                        const badgeClass =
                          DEPOSIT_STATUS_COLORS[d.refundStatus] ??
                          'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
                        return (
                          <div
                            key={rowKey}
                            className="py-1 text-sm"
                            data-testid={`deposit-row-${rowKey}`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground truncate mr-2">
                                {d.equipmentName ?? t('rental.depositUnnamed')}
                              </span>
                              <span className="flex items-center gap-2 shrink-0">
                                {/* 40-kör: gépenkénti azonnali elszámolás chip (PR-0 settledAt read-vetülete).
                                    41-kör FE-3 ①: relatív idő a chipen (pult-UX), abszolút a tooltipben. */}
                                {d.settledAt != null && (
                                  <span
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
                                    data-testid={`deposit-settled-chip-${rowKey}`}
                                    title={formatDateSmart(d.settledAt)}
                                  >
                                    {t('rental.depositSettledAt', {
                                      date: formatRelativeTime(d.settledAt),
                                    })}
                                  </span>
                                )}
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}
                                  data-testid={`deposit-status-badge-${rowKey}`}
                                >
                                  {t(`rental.depositStatus.${d.refundStatus}`)}
                                </span>
                                <span className="font-medium text-foreground">
                                  {formatCurrency(d.amount)}
                                </span>
                              </span>
                            </div>
                            {/* Slice A+B+C: per-gép refund + retain + partial + transfer-to-worksheet akciók */}
                            <DepositActions
                              deposit={d}
                              customerId={rental.customerId}
                              onActionComplete={refetch}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Late fee */}
                  {rental.lateFeeAmount > 0 && (
                    <div className="flex items-center justify-between py-2 border-b dark:border-slate-600 text-red-600 dark:text-red-400">
                      <span>{t('rental.lateFee')}</span>
                      <span className="font-medium">{formatCurrency(rental.lateFeeAmount)}</span>
                    </div>
                  )}

                  {/* Payment status */}
                  <div className="mt-4 flex gap-4">
                    <div className="flex-1 rounded-lg bg-green-50 dark:bg-green-900/30 p-3 text-center">
                      <p className="text-sm text-green-600 dark:text-green-400">
                        {t('rental.rentalFee')}
                      </p>
                      <p className="text-lg font-bold text-green-700 dark:text-green-300">
                        {formatCurrency(rental.totalAmount)}
                      </p>
                    </div>
                    <div className="flex-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 p-3 text-center">
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        {t('rental.depositPaid')}
                      </p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">
                        {formatCurrency(rental.depositPaid)}
                      </p>
                    </div>
                  </div>

                  {/* PV-BERLES-001: Részleges-fizetés összesítő (read-DTO #679) */}
                  {rental.outstanding != null && (
                    <>
                      {/* Breakdown sorok */}
                      {rental.paidAmount != null && (
                        <div
                          className="flex items-center justify-between py-2 border-b dark:border-slate-600"
                          data-testid="rental-paid-amount"
                        >
                          <span className="text-muted-foreground">
                            {t('rental.payment.paidAmount')}
                          </span>
                          <span className="font-medium text-foreground">
                            {formatCurrency(rental.paidAmount)}
                          </span>
                        </div>
                      )}
                      {rental.loyaltyRedeemAmount != null && rental.loyaltyRedeemAmount > 0 && (
                        <div
                          className="flex items-center justify-between py-2 border-b dark:border-slate-600"
                          data-testid="rental-loyalty-redeem"
                        >
                          <span className="text-muted-foreground">
                            {t('rental.payment.loyaltyDiscount')}
                          </span>
                          <span className="font-medium text-foreground">
                            {formatCurrency(rental.loyaltyRedeemAmount)}
                          </span>
                        </div>
                      )}

                      {/* Outstanding-fókusz card */}
                      <div
                        className={`mt-4 rounded-lg p-4 text-center ${
                          rental.outstanding === 0
                            ? 'bg-green-50 dark:bg-green-900/30'
                            : 'bg-orange-50 dark:bg-orange-900/30'
                        }`}
                        data-testid="rental-outstanding"
                      >
                        <p
                          className={`text-sm ${
                            rental.outstanding === 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-orange-600 dark:text-orange-400'
                          }`}
                        >
                          {rental.outstanding === 0
                            ? t('rental.payment.fullyPaid')
                            : t('rental.payment.outstandingBalance')}
                        </p>
                        <p
                          className={`text-2xl font-bold ${
                            rental.outstanding === 0
                              ? 'text-green-700 dark:text-green-300'
                              : 'text-orange-700 dark:text-orange-300'
                          }`}
                        >
                          {rental.outstanding === 0
                            ? formatCurrency(0)
                            : formatCurrency(rental.outstanding)}
                        </p>
                      </div>

                      {/* „Fizetés rögzítése" gomb — csak ACTIVE/OVERDUE + outstanding>0 */}
                      {rental.outstanding > 0 &&
                        (rental.status === RentalStatus.ACTIVE ||
                          rental.status === RentalStatus.OVERDUE) && (
                          <div className="mt-4">
                            {!paymentOpen ? (
                              <Button
                                variant="outline"
                                className="w-full"
                                data-testid="record-payment-btn"
                                onClick={() => setPaymentOpen(true)}
                              >
                                {t('rental.payment.recordButton')}
                              </Button>
                            ) : (
                              <RentalPaymentForm
                                rentalId={rental.id}
                                outstanding={rental.outstanding}
                                onSuccess={_newOutstanding => {
                                  setPaymentOpen(false);
                                  refetch();
                                }}
                                onCancel={() => setPaymentOpen(false)}
                              />
                            )}
                          </div>
                        )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('rental.quickInfo')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 text-primary">
                      📅
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('rental.startDate')}</p>
                      <p className="font-medium text-foreground">
                        {formatDateSmart(rental.startDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 text-primary">
                      🏁
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('rental.expiryDate')}</p>
                      <p className="font-medium text-foreground">
                        {formatDateSmart(rental.expectedReturnDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-full ${
                        daysRemaining < 0
                          ? 'bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400'
                          : daysRemaining <= 2
                            ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400'
                            : 'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
                      }`}
                    >
                      ⏳
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {daysRemaining < 0 ? t('status.expired') : t('rental.remaining')}
                      </p>
                      <p
                        className={`font-medium ${
                          daysRemaining < 0
                            ? 'text-red-600 dark:text-red-400'
                            : daysRemaining <= 2
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-foreground'
                        }`}
                      >
                        {daysRemaining < 0
                          ? t('rental.daysAgo', { days: Math.abs(daysRemaining) })
                          : t('rental.daysLeft', { days: daysRemaining })}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('common.actions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(rental.status === RentalStatus.ACTIVE ||
                    rental.status === RentalStatus.OVERDUE) && (
                    <>
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={() => navigate(`/rental/return?id=${rental.id}`)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t('rental.returnRental')}
                      </Button>
                      <Button variant="outline" className="w-full" onClick={handleExtensionOpen}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-2 h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {t('rental.extension')}
                      </Button>
                    </>
                  )}
                  <Button variant="outline" className="w-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                      />
                    </svg>
                    {t('rental.printContract')}
                  </Button>
                  <Button variant="ghost" className="w-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    {t('rental.sendEmail')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Timeline Card */}
            <Card>
              <CardHeader>
                <CardTitle>{t('rental.history')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {timelineEvents.map((event, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-sm">
                        {event.icon}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-foreground text-sm">{event.title}</p>
                        <p className="text-xs text-muted-foreground">{event.description}</p>
                        <p className="text-xs text-muted-foreground/70 mt-0.5">
                          {formatDateTime(event.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            {rental.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('common.notes')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">{rental.notes}</p>
                </CardContent>
              </Card>
            )}

            {/* FE-D2 — kiadáskori állapot-fotók (HANDOVER) */}
            <Card>
              <CardContent className="pt-6">
                <HandoverPhotoUploader rentalId={rental.id} kind="HANDOVER" />
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Extension Dialog (Story 46-12) */}
      {extensionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setExtensionOpen(false)}
            data-testid="extension-overlay"
          />
          <div
            className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900"
            role="dialog"
            aria-modal="true"
            aria-labelledby="extension-dialog-title"
            data-testid="extension-dialog"
          >
            <h2 id="extension-dialog-title" className="mb-4 text-lg font-bold text-foreground">
              {t('rental.extensionDialog.title')}
            </h2>

            <div className="space-y-4">
              {/* Current end date info */}
              <div className="rounded-lg bg-muted p-3">
                <p className="text-sm text-muted-foreground">
                  {t('rental.extensionDialog.currentExpiry')}:{' '}
                  <span className="font-medium text-foreground">
                    {formatDateSmart(rental.expectedReturnDate)}
                  </span>
                </p>
              </div>

              {/* New end date picker */}
              <div>
                <label
                  htmlFor="extension-date"
                  className="mb-1 block text-sm font-medium text-foreground"
                >
                  {t('rental.extensionDialog.newExpiryDate')}
                </label>
                <Input
                  id="extension-date"
                  type="date"
                  value={extensionDate}
                  onChange={e => setExtensionDate(e.target.value)}
                  min={rental.expectedReturnDate.split('T')[0] ?? ''}
                  data-testid="extension-date-input"
                />
              </div>

              {/* Extension preview */}
              {extensionPreview && (
                <div
                  className="space-y-2 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/30"
                  data-testid="extension-preview"
                >
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('rental.extensionDialog.additionalDays')}:
                    </span>
                    <span className="font-medium text-foreground">
                      +{extensionPreview.additionalDays} {t('rental.extensionDialog.days')}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t('rental.extensionDialog.surcharge')} (
                      {formatCurrency(extensionPreview.dailyRate)}/{t('rental.extensionDialog.day')}
                      ):
                    </span>
                    <span className="font-medium text-foreground">
                      {formatCurrency(extensionPreview.additionalAmount)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2 text-sm font-bold dark:border-slate-600">
                    <span className="text-foreground">
                      {t('rental.extensionDialog.surchargeWithVat')}:
                    </span>
                    <span className="text-green-700 dark:text-green-300">
                      {formatCurrency(extensionPreview.additionalAmountWithVat)}
                    </span>
                  </div>
                  {extensionPreview.discountChanged && (
                    <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                      {t('rental.extensionDialog.discountTierChange')}:{' '}
                      {extensionPreview.originalTierName} → {extensionPreview.newTierName}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('rental.extensionDialog.priceDisclaimer')}
                  </p>
                </div>
              )}

              {extensionDate && !extensionPreview && (
                <p
                  className="text-sm text-red-600 dark:text-red-400"
                  data-testid="extension-date-error"
                >
                  {t('rental.extensionDialog.dateError')}
                </p>
              )}

              {extensionError && (
                <p
                  className="text-sm text-red-600 dark:text-red-400"
                  role="alert"
                  data-testid="extension-submit-error"
                >
                  {extensionError}
                </p>
              )}

              {/* Reason */}
              <div>
                <label
                  htmlFor="extension-reason"
                  className="mb-1 block text-sm font-medium text-foreground"
                >
                  {t('rental.extensionDialog.reasonLabel')}
                </label>
                <textarea
                  id="extension-reason"
                  value={extensionReason}
                  onChange={e => setExtensionReason(e.target.value)}
                  className="w-full rounded-md border p-2 text-sm dark:border-slate-600 dark:bg-slate-800 text-foreground"
                  rows={2}
                  placeholder={t('rental.extensionDialog.reasonPlaceholder')}
                  aria-label={t('rental.extensionDialog.reasonLabel')}
                  data-testid="extension-reason-input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => setExtensionOpen(false)} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleExtensionSubmit}
                disabled={!extensionPreview || extensionSubmitting}
                className="flex-1 bg-primary hover:bg-primary/90"
                data-testid="extension-submit"
              >
                {extensionSubmitting ? t('common.processing') : t('rental.extension')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
